import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { IssueCard } from '@/types/lcopilot';
import { AlertCircle, AlertTriangle, Info, Ban, FileWarning, Lightbulb, Sparkles, CheckCircle, Scale, BookOpen, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { HowToFixSection, type EmailDraftContext } from './HowToFixSection';

/**
 * Humanize rule IDs for display
 */
function humanizeRuleId(ruleId: string): string {
  if (!ruleId) return "";
  
  const categoryMap: Record<string, string> = {
    "CROSSDOC": "Cross-Document Check",
    "AI-MISSING": "AI Detection",
    "AI-": "AI Validation",
    "BL-": "Bill of Lading",
    "INV-": "Invoice",
    "LC-": "LC Requirement",
    "COO-": "Certificate of Origin",
    "PL-": "Packing List",
    "INS-": "Insurance",
    "SANCTIONS": "Sanctions Screening",
    "UCP-": "UCP600 Rule",
    "ISBP-": "ISBP 821 Rule",
  };
  
  for (const [prefix, label] of Object.entries(categoryMap)) {
    if (ruleId.toUpperCase().startsWith(prefix)) {
      return label;
    }
  }
  
  return ruleId.replace(/-/g, " ").replace(/_/g, " ").replace(/\d+$/, "").trim();
}

// NOTE: UCP600 and ISBP 821 descriptions are now provided by the backend
// via issue.ucpDescription and issue.isbpDescription fields.
// See: apps/api/app/constants/compliance_references.py

// Tolerance source explanations
const TOLERANCE_SOURCE_EXPLANATIONS: Record<string, { title: string; description: string; reference?: string }> = {
  'LC_SPECIFIED': {
    title: 'LC-Specified Tolerance',
    description: 'This tolerance was explicitly stated in your Letter of Credit. It takes precedence over UCP600 defaults.',
    reference: 'As per LC terms',
  },
  'UCP_DEFAULT': {
    title: 'UCP600 Article 30 Tolerance',
    description: 'Standard ±5% tolerance applies to quantity/unit price when LC uses "about" or "approximately", or when no tolerance is specified for goods sold in bulk.',
    reference: 'UCP600 Article 30(a)',
  },
  'BANK_POLICY': {
    title: 'Issuing Bank Policy',
    description: 'The issuing bank has specific tolerance policies that differ from standard UCP600. These are applied based on the bank\'s historical practices.',
    reference: 'Bank-specific',
  },
  'USER_OVERRIDE': {
    title: 'Manual Override',
    description: 'You have manually specified this tolerance. Please ensure it aligns with your LC terms.',
    reference: 'User-defined',
  },
  'INCOTERM_ADJUSTMENT': {
    title: 'Incoterms Adjustment',
    description: 'Tolerance adjusted based on the Incoterm used (CIF, FOB, etc.) which affects insurance and freight calculations.',
    reference: 'Trade practice',
  },
};

// ToleranceExplanationBadge component with hover tooltip
function ToleranceExplanationBadge({ tolerance }: { tolerance: { tolerance_percent: number; source: string; explicit?: boolean } }) {
  const sourceInfo = TOLERANCE_SOURCE_EXPLANATIONS[tolerance.source] || {
    title: 'Applied Tolerance',
    description: `A ${tolerance.tolerance_percent}% tolerance was applied to this check.`,
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="gap-1 text-xs bg-[#B2F273]/10 text-[#00261C] dark:text-[#B2F273] border-[#B2F273]/40 cursor-help"
          >
            <Scale className="w-3 h-3" />
            ±{tolerance.tolerance_percent}% tolerance
            {tolerance.explicit && <CheckCircle className="w-3 h-3 ml-0.5" />}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm p-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-[#00382E] dark:text-[#B2F273]" />
              <p className="font-semibold text-sm">{sourceInfo.title}</p>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {sourceInfo.description}
            </p>
            {sourceInfo.reference && (
              <p className="text-xs text-[#00382E] dark:text-[#B2F273] font-medium flex items-center gap-1">
                <BookOpen className="w-3 h-3" />
                {sourceInfo.reference}
              </p>
            )}
            {tolerance.explicit && (
              <p className="text-xs text-[#00261C] dark:text-[#B2F273] bg-[#B2F273]/10 px-2 py-1 rounded">
                ✓ Explicitly stated in LC
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Business impact mapping - what will the bank do?
const BUSINESS_IMPACT = {
  critical: {
    label: 'Will Cause Rejection',
    description: 'Bank will refuse documents',
    color: 'bg-rose-600 text-white',
    Icon: Ban,
  },
  major: {
    label: 'Likely Discrepancy',
    description: 'Bank will issue discrepancy notice',
    color: 'bg-amber-500 text-white',
    Icon: FileWarning,
  },
  medium: {
    label: 'May Cause Query',
    description: 'Bank may request clarification',
    color: 'bg-amber-400 text-amber-900',
    Icon: AlertTriangle,
  },
  minor: {
    label: 'Bank Discretion',
    description: 'Usually accepted, depends on bank',
    color: 'bg-muted text-muted-foreground',
    Icon: Lightbulb,
  },
} as const;

const COMPLIANCE_IMPACT = {
  critical: {
    label: 'Compliance Hold',
    description: 'Internal compliance review required before proceeding',
    color: 'bg-[#00261C] text-[#B2F273]',
    Icon: ShieldAlert,
  },
  major: {
    label: 'Compliance Alert',
    description: 'Escalate to internal compliance review',
    color: 'bg-[#00382E] text-[#B2F273]',
    Icon: ShieldAlert,
  },
  medium: {
    label: 'Manual Compliance Review',
    description: 'Review required before submission decision',
    color: 'bg-[#B2F273]/20 text-[#00261C] dark:text-[#B2F273]',
    Icon: ShieldAlert,
  },
  minor: {
    label: 'Compliance Note',
    description: 'Monitor within internal compliance workflow',
    color: 'bg-[#B2F273]/10 text-[#00382E] dark:text-[#B2F273]',
    Icon: ShieldAlert,
  },
} as const;

const severityTokens = {
  critical: {
    label: 'Critical',
    color: '#E24A4A',
    bg: 'bg-[#E24A4A]/10 border-[#E24A4A]/30 text-[#E24A4A]',
    Icon: AlertCircle,
  },
  major: {
    label: 'Major',
    color: '#E24A4A',
    bg: 'bg-[#E24A4A]/10 border-[#E24A4A]/30 text-[#E24A4A]',
    Icon: AlertCircle,
  },
  medium: {
    label: 'Medium',
    color: '#F0A500',
    bg: 'bg-[#F0A500]/10 border-[#F0A500]/30 text-[#F0A500]',
    Icon: AlertTriangle,
  },
  minor: {
    label: 'Minor',
    color: '#6BBF59',
    bg: 'bg-[#6BBF59]/10 border-[#6BBF59]/30 text-[#2F7E2F]',
    Icon: Info,
  },
} as const;

type ExporterIssueCardProps = {
  issue: IssueCard;
  normalizedSeverity: 'critical' | 'major' | 'minor';
  documentStatusMap: Map<string, { status?: string; type?: string }>;
  fallbackId: string;
  lcNumber?: string;
  companyName?: string;
  onDraftEmail?: (context: EmailDraftContext) => void;
};

const formatValue = (value?: string) => {
  if (!value || value === '—') {
    return '—';
  }
  return value;
};

// Safely convert any value to string to prevent React Error #31
const safeString = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    if ('types' in value && Array.isArray(value.types)) {
      return value.types.join(', ');
    }
    return JSON.stringify(value);
  }
  return String(value);
};

export function ExporterIssueCard({
  issue,
  normalizedSeverity,
  documentStatusMap,
  fallbackId,
  lcNumber,
  companyName,
  onDraftEmail,
}: ExporterIssueCardProps) {
  const severity = severityTokens[normalizedSeverity] ?? severityTokens.minor;
  const Icon = severity.Icon;
  const documentNames = (issue.documents ?? []).filter(Boolean);
  const bucket = (issue as any).bucket ?? 'Document-Level Discrepancies';
  const workflowLane =
    (issue as any).workflow_lane ??
    (bucket === 'Compliance / Risk Review'
      ? 'compliance_review'
      : bucket === 'Extraction / Manual Review'
      ? 'manual_review'
      : 'documentary_review');
  const severityDisplay = (issue as any).severity_display ?? (normalizedSeverity === 'critical' ? 'High-likelihood discrepancy' : normalizedSeverity === 'major' ? 'Likely discrepancy' : 'Review required');
  const lcBasis = (issue as any).lc_basis ?? issue.ucpReference ?? issue.isbpReference ?? issue.rule ?? 'LC examination baseline';
  const examinerNote = (issue as any).examiner_note ?? issue.description ?? 'Review against LC terms and presented documents.';
  const fixOwner = (issue as any).fix_owner ?? (issue as any).remediation_owner ?? 'Unknown';
  const nextAction = (issue as any).next_action ?? issue.suggestion ?? 'Correct and revalidate before submission.';
  const confidence = (issue as any).confidence ?? issue.extraction_confidence;
  const ownerLabel = workflowLane === 'documentary_review' ? 'Fix owner' : 'Review owner';
  const actionLabel = workflowLane === 'documentary_review' ? 'Next Action' : 'Review Action';
  const basisLabel =
    workflowLane === 'compliance_review'
      ? 'Compliance Basis'
      : workflowLane === 'manual_review'
      ? 'Review Basis'
      : 'LC Basis / Clause';
  const noteLabel =
    workflowLane === 'compliance_review'
      ? 'Compliance Note'
      : workflowLane === 'manual_review'
      ? 'Review Note'
      : 'Examiner Note';

  const buildDocumentBadgeClass = (status?: string) => {
    if (status === 'success') return 'bg-success/10 text-success border-success/30';
    if (status === 'error') return 'bg-destructive/10 text-destructive border-destructive/30';
    return 'bg-warning/10 text-warning border-warning/30';
  };

  // Get business impact for this severity
  const isComplianceIssue = bucket === 'Compliance / Risk Review';
  const impact = isComplianceIssue
    ? COMPLIANCE_IMPACT[normalizedSeverity] ?? COMPLIANCE_IMPACT.minor
    : BUSINESS_IMPACT[normalizedSeverity] ?? BUSINESS_IMPACT.minor;
  const ImpactIcon = impact.Icon;
  
  // Check if this is an AI-detected issue
  const isAIDetected = 
    (issue as any).ruleset_domain === 'icc.lcopilot.ai_validation' || 
    (issue as any).auto_generated === true ||
    (issue.rule?.startsWith('AI-') ?? false);

  return (
    <Card
      key={fallbackId}
      className="shadow-sm border border-border/70 bg-card transition-all duration-300 hover:shadow-lg animate-in fade-in-50"
      data-testid={`issue-card-${fallbackId}`}
    >
      <CardHeader className="space-y-2 pb-3">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-lg font-semibold text-foreground">{issue.title ?? 'Review Required'}</CardTitle>
          <div className="flex items-center gap-2">
            {/* AI Detection Badge */}
            {isAIDetected && (
              <Badge
                className="gap-1 text-xs font-semibold bg-gradient-to-r from-[#00261C] to-[#00382E] text-[#B2F273]"
                title="Detected by AI Validation Engine"
              >
                <Sparkles className="w-3 h-3" />
                AI Detected
              </Badge>
            )}
            {/* Business Impact Badge - What will the bank do? */}
            <Badge
              className={cn('gap-1 text-xs font-semibold', impact.color)}
              title={impact.description}
            >
              <ImpactIcon className="w-3 h-3" />
              {impact.label}
            </Badge>
            <Badge
              data-testid={`severity-${fallbackId}`}
              data-icon={normalizedSeverity}
              className={cn('gap-1 border text-xs font-semibold', severity.bg)}
            >
              <Icon className="w-3.5 h-3.5" />
              {severityDisplay}
            </Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="text-xs">{bucket}</Badge>
          <Badge variant="outline" className="text-xs">{ownerLabel}: {fixOwner}</Badge>
          {typeof confidence === 'number' && (
            <Badge variant="outline" className="text-xs">Confidence: {(confidence * 100).toFixed(0)}%</Badge>
          )}
        </div>
        {issue.description ? (
          <CardDescription className="text-sm text-muted-foreground">{issue.description}</CardDescription>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Bank Examiner Message - Professional format for banks (from V2) */}
        {(issue.ucpReference || issue.isbpReference) && (
          <div className="p-3 bg-muted rounded-lg border border-border">
            <p className="text-sm font-medium text-muted-foreground">
              Bank Examiner Message:
            </p>
            <p className="text-sm text-foreground mt-1 font-mono">
              {issue.title}. Per {issue.ucpReference ? `UCP600 ${issue.ucpReference.replace('UCP600 Article ', 'Art. ').replace('UCP600 ', '')}` : ''}{issue.ucpReference && issue.isbpReference ? '; ' : ''}{issue.isbpReference ? `ISBP 821 ${issue.isbpReference.replace('ISBP 821 ', '¶')}` : ''}.
            </p>
          </div>
        )}
        
        {/* Citation Badges with Tooltips (from V2) */}
        {(issue.ucpReference || issue.isbpReference) && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <BookOpen className="h-4 w-4" />
              Regulatory Citations
            </p>
            <div className="flex flex-wrap gap-2">
              <TooltipProvider>
                {issue.ucpReference && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="secondary"
                        className="cursor-help bg-[#B2F273]/15 text-[#00261C] hover:bg-[#B2F273]/25 dark:bg-[#00382E] dark:text-[#B2F273] dark:hover:bg-[#00382E]/80"
                      >
                        UCP600 {issue.ucpReference.replace('UCP600 Article ', 'Art. ').replace('UCP600 ', '')}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-medium">{issue.ucpReference}</p>
                      <p className="text-xs text-muted-foreground">
                        {issue.ucpDescription || 'ICC Uniform Customs and Practice for Documentary Credits'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {issue.isbpReference && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="secondary"
                        className="cursor-help bg-muted text-muted-foreground hover:bg-muted/70 border border-border"
                      >
                        ISBP 821 {issue.isbpReference.replace('ISBP 821 ', '¶')}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p className="font-medium">{issue.isbpReference}</p>
                      <p className="text-xs text-muted-foreground">
                        {issue.isbpDescription || 'ICC International Standard Banking Practice'}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
          </div>
        )}
        
        {documentNames.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {documentNames.map((name) => {
              const meta = documentStatusMap.get(name);
              return (
                <Badge key={name} variant="outline" className={cn('text-xs', buildDocumentBadgeClass(meta?.status))}>
                  {meta?.type ? `${safeString(meta.type)}: ` : ''}
                  {name}
                </Badge>
              );
            })}
          </div>
        )}

        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-md border p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{basisLabel}</p>
            <p className="text-sm mt-1">{lcBasis}</p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{noteLabel}</p>
            <p className="text-sm mt-1">{examinerNote}</p>
          </div>
        </div>

        {/* LC Clause Quote — shows the exact clause from 46A/47A */}
        {(issue as any).lc_clause && (
          <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-xs font-medium text-amber-700 dark:text-amber-300 uppercase tracking-wide flex items-center gap-1">
              <Scale className="w-3.5 h-3.5" />
              LC Clause
            </p>
            <p className="mt-1 text-sm text-amber-900 dark:text-amber-100 italic">
              &ldquo;{(issue as any).lc_clause}&rdquo;
            </p>
          </div>
        )}

        {/* Color-coded Expected/Found (from V2) */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="p-3 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <p className="text-xs font-medium text-green-700 dark:text-green-300 uppercase tracking-wide">Expected</p>
            <pre className="mt-1 font-mono text-sm leading-relaxed whitespace-pre-wrap text-green-900 dark:text-green-100">
              {formatValue(issue.expected)}
            </pre>
          </div>
          <div className="p-3 bg-red-50 dark:bg-red-950 rounded-lg border border-red-200 dark:border-red-800">
            <p className="text-xs font-medium text-red-700 dark:text-red-300 uppercase tracking-wide">Found</p>
            <pre className="mt-1 font-mono text-sm leading-relaxed whitespace-pre-wrap text-red-900 dark:text-red-100">
              {formatValue(issue.actual)}
            </pre>
          </div>
        </div>

        {/* Impact — what happens if this isn't fixed */}
        {(issue as any).impact && (
          <div className="p-3 bg-rose-50 dark:bg-rose-950/30 rounded-lg border border-rose-200 dark:border-rose-800">
            <p className="text-xs font-medium text-rose-700 dark:text-rose-300 uppercase tracking-wide flex items-center gap-1">
              <Ban className="w-3.5 h-3.5" />
              Bank Impact
            </p>
            <p className="mt-1 text-sm text-rose-900 dark:text-rose-100">{(issue as any).impact}</p>
          </div>
        )}
        
        {/* Tolerance & Confidence Metadata */}
        {(issue.tolerance_applied || issue.extraction_confidence !== undefined) && (
          <div className="flex flex-wrap gap-2 mt-2">
            {issue.tolerance_applied && 
             typeof issue.tolerance_applied.tolerance_percent === 'number' && 
             typeof issue.tolerance_applied.source === 'string' && (
              <ToleranceExplanationBadge tolerance={issue.tolerance_applied as { tolerance_percent: number; source: string; explicit?: boolean }} />
            )}
            {issue.extraction_confidence !== undefined && issue.extraction_confidence < 0.7 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "gap-1 text-xs cursor-help",
                        issue.extraction_confidence < 0.5 
                          ? "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30"
                          : "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/30"
                      )}
                    >
                      <AlertTriangle className="w-3 h-3" />
                      OCR: {(issue.extraction_confidence * 100).toFixed(0)}%
                      {issue.extraction_confidence < 0.5 && " - Verify manually"}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    <p className="font-medium mb-1">Extraction Confidence: {(issue.extraction_confidence * 100).toFixed(0)}%</p>
                    <p className="text-xs text-muted-foreground">
                      {issue.extraction_confidence < 0.5 
                        ? "Low confidence extraction. The OCR may have misread this field. Please verify the value manually against your original document."
                        : "Medium confidence extraction. The value is likely correct but may need verification for critical fields."}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
        <div className="bg-[#B2F273]/10 dark:bg-[#00382E] border border-[#B2F273]/30 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-[#00382E] dark:text-[#B2F273] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#00261C] dark:text-[#B2F273] mb-1">
                {actionLabel}
              </p>
              <p className="text-sm text-foreground">{nextAction}</p>
            </div>
          </div>
        </div>
        
        {/* How to Fix Section - Actionable guidance */}
        <HowToFixSection 
          issue={issue}
          lcNumber={lcNumber}
          companyName={companyName}
          onDraftEmail={onDraftEmail}
        />
        
        {/* Rule Category - shows the validation source */}
        {issue.rule && (
          <div className="text-xs text-muted-foreground pt-2 border-t border-border/50 flex items-center gap-2">
            <span className="font-medium">{humanizeRuleId(issue.rule)}</span>
            <span className="font-mono text-[10px] text-muted-foreground/60">({issue.rule})</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
